import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import './Contact.css';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  consent: boolean;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

export function Contact() {
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    consent: false,
  });
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleConsent(e: React.ChangeEvent<HTMLInputElement>) {
    setForm(prev => ({ ...prev, consent: e.target.checked }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('The contact service is unavailable. Please try again later.');
      }

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <div className="contact">
      <Hero
        title="Contact"
        subtitle="Questions, thoughts, or just want to connect?"
        size="small"
      />

      <section className="contact__content">
        <div className="container container--narrow">
          {status === 'success' ? (
            <div className="contact__success">
              <i className="fas fa-check-circle"></i>
              <h2>Message Sent</h2>
              <p>
                Thank you for reaching out. I'll get back to you as soon as I can.
              </p>
              <button
                className="btn btn--outline btn--medium"
                onClick={() => {
                  setStatus('idle');
                  setForm({ name: '', email: '', subject: '', message: '', consent: false });
                }}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form className="contact__form" onSubmit={handleSubmit}>
              <div className="contact__field">
                <label htmlFor="contact-name">Name <span className="contact__required">*</span></label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                />
              </div>

              <div className="contact__field">
                <label htmlFor="contact-email">Email <span className="contact__required">*</span></label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                />
              </div>

              <div className="contact__field">
                <label htmlFor="contact-subject">Subject</label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </div>

              <div className="contact__field">
                <label htmlFor="contact-message">Message <span className="contact__required">*</span></label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="What's on your mind?"
                />
              </div>

              <div className="contact__consent">
                <input
                  id="contact-consent"
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={handleConsent}
                  required
                />
                <label htmlFor="contact-consent" className="contact__consent-label">
                  By submitting this form, you agree to our{' '}
                  <Link to="/privacy">Privacy Policy</Link> and consent to the processing of your
                  information for communication purposes.
                  <span className="contact__required"> *</span>
                </label>
              </div>

              {status === 'error' && (
                <div className="contact__error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{errorMessage}</span>
                </div>
              )}

              <button
                type="submit"
                className="btn btn--primary btn--large contact__submit"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i>
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
